import React from 'react';
import { LodgingCard, MealCard } from '@cubemeet/ui';
import { TouchableOpacity, View } from 'react-native';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import L from 'leaflet';
import { buildMetaPairs, arrayify, coalesceText, mergePayload, ORANGE } from './itineraryUtils';
import PlatformIcon from './WebIcon';

interface Props {
  it: any;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  onOpenPoi?: (payload: any) => void;
  dayDate?: string;
  onImageClick?: (images: any[], startIndex: number) => void;
}

const LodgingAdapter: React.FC<Props> = ({ it, isExpanded = false, onOpenPoi, dayDate, onImageClick }) => {
  // Helper: safely extract a readable string from various shapes
  const extractString = (value: any): string | undefined => {
    if (value == null) return undefined;
    if (typeof value === 'string') return value.trim() || undefined;
    if (Array.isArray(value)) {
      const parts = value.map(v => extractString(v)).filter(Boolean) as string[];
      return parts.length ? parts.join(' ') : undefined;
    }
    if (typeof value === 'object') {
      const candidates = ['formatted_address', 'formattedAddress', 'address', 'display', 'label', 'name', 'title', 'short_name', 'description', 'locality'];
      for (const k of candidates) {
        if (value[k]) {
          const s = extractString(value[k]);
          if (s) return s;
        }
      }
      const values = Object.values(value).map(v => (typeof v === 'string' ? v : (typeof v === 'number' ? String(v) : ''))).filter(Boolean);
      if (values.length) return values.join(' ');
      return undefined;
    }
    return String(value).trim() || undefined;
  };

  const hasCardObj = Boolean(it?.card && Object.keys(it.card || {}).length > 0) || Boolean(it?.card_id || it?.cardId || (it?.meta && (it.meta.cardId || it.meta.card_id)));
  const mergedPayload = hasCardObj ? mergePayload(it) : (it?.custom_payload || it?.inline_payload || it?.payload || {});
  // Only use rich card layout when there is an external card AND the card record provides a cover image
  // OR if we have coordinates but no image (to show map preview)
  const hasCardCover = Boolean(it?.card && (it.card.cover_image || (Array.isArray(it.card.image_gallery) && it.card.image_gallery.length > 0)));
  
  const lat = typeof mergedPayload.lat === 'number' ? mergedPayload.lat : 
              (typeof mergedPayload.latitude === 'number' ? mergedPayload.latitude : 
              (typeof it?.card?.latitude === 'number' ? it.card.latitude : 
              (typeof it?.card?.lat === 'number' ? it.card.lat : undefined)));
              
  const lng = typeof mergedPayload.lng === 'number' ? mergedPayload.lng : 
              (typeof mergedPayload.longitude === 'number' ? mergedPayload.longitude : 
              (typeof it?.card?.longitude === 'number' ? it.card.longitude : 
              (typeof it?.card?.lng === 'number' ? it.card.lng : undefined)));
  const hasCoordinates = lat !== undefined && lng !== undefined;

  const useRichCard = (hasCardObj && hasCardCover) || hasCoordinates;
  const title = coalesceText(it?.title, it?.card?.title, mergedPayload.title) || '未命名';
  // 優先使用 it.note（行程表副本的描述），再使用 it.description（前端更新後的）
  const description = coalesceText(
    it?.note,
    it?.description,
    mergedPayload.description,
    it?.card?.description,
    mergedPayload.summary,
    mergedPayload.intro,
  );

  // Prioritize mergedPayload.address (includes custom_payload override) over it.card.address
  const address = extractString(mergedPayload?.address || it?.card?.address);
  const hotelNameCandidate = coalesceText(mergedPayload.hotelName || mergedPayload.title || mergedPayload.name);
  const normalize = (s?: string) => (s ? s.toLowerCase().replace(/\s+/g, ' ').trim() : '');
  const hotelName = hotelNameCandidate && normalize(hotelNameCandidate) !== normalize(title) ? hotelNameCandidate : undefined;

  const metaPairs = buildMetaPairs(it, mergedPayload);
  const transportNote = (() => {
    const entry = metaPairs.find((pair) => {
      if (!pair || !pair.label) return false;
      const label = String(pair.label).trim();
      return label === '交通方式' || label === '交通';
    });
    if (!entry || !entry.value) return undefined;
    return String(entry.value).trim() || undefined;
  })();

  const payloadForModal = {
    title,
    address,
    description,
    metaPairs,
    rawDebug: { rawMergedPayload: mergedPayload },
    card: it?.card,
    id: it?.card?.id || it?.meta?.cardId || it?.cardId,
    cardId: it?.card?.id || it?.meta?.cardId || it?.cardId,
  };

  // If this item has an external card, render a richer card (image + details) using MealCard layout for visual parity.
  let card;
  if (useRichCard) {
    const imageUrl = it?.card?.cover_image || mergedPayload?.cover_image || mergedPayload?.image || mergedPayload?.image_url || (Array.isArray(it?.card?.image_gallery) && it.card.image_gallery.length ? it.card.image_gallery[0] : undefined) || (Array.isArray(mergedPayload?.image_gallery) && mergedPayload.image_gallery.length ? mergedPayload.image_gallery[0] : undefined) || undefined;
    
    // If imageUrl is the default placeholder, treat it as undefined so we can show the map instead
    const isPlaceholder = imageUrl && (imageUrl.includes('card-header-default.jpg') || imageUrl.includes('placeholders/card-header-default'));
    const effectiveImageUrl = isPlaceholder ? undefined : imageUrl;

    const firstChar = (title || '').charAt(0).toUpperCase();
    const customIcon = L.divIcon({
      className: 'custom-map-marker',
      html: `<div style="
        background-color: #F97316;
        color: white;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 14px;
        font-weight: bold;
        border: 2px solid white;
        box-shadow: 0 2px 4px rgba(0,0,0,0.2);
      ">${firstChar}</div>`,
      iconSize: [24, 24],
      iconAnchor: [12, 12],
    });

    const mapPreview = (!effectiveImageUrl && hasCoordinates) ? (
      <div style={{ height: '100%', width: '100%', zIndex: 0, position: 'absolute', top: 0, left: 0 }}>
        <MapContainer 
          center={[lat!, lng!]} 
          zoom={15} 
          style={{ height: '100%', width: '100%' }} 
          zoomControl={false} 
          dragging={false}
          scrollWheelZoom={false}
          doubleClickZoom={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <Marker position={[lat!, lng!]} icon={customIcon} />
        </MapContainer>
      </div>
    ) : undefined;

    const venue = extractString(coalesceText(it?.card?.location, mergedPayload.location, mergedPayload.address));
    card = (
      <MealCard
        title={title}
        note={description}
        restaurantName={hotelName}
        venue={venue}
        imageUrl={effectiveImageUrl}
        address={address}
        actionIcon={<PlatformIcon name="hotel" size={16} color={ORANGE} />}
        transportNote={transportNote}
        dayDate={dayDate}
        headerContent={mapPreview}
      />
    );
  } else {
    card = (
      <LodgingCard
        title={title}
        note={description}
        hotelName={hotelName}
        address={address}
        checkIn={mergedPayload.checkIn || mergedPayload.check_in}
        checkOut={mergedPayload.checkOut || mergedPayload.check_out}
        adContent={undefined}
        transportNote={transportNote}
      />
    );
  }

  if (typeof onOpenPoi === 'function') {
    return (
      <TouchableOpacity activeOpacity={0.8} onPress={() => onOpenPoi(payloadForModal)}>
        {card}
      </TouchableOpacity>
    );
  }

  return card;
};

export default LodgingAdapter;
